import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import ModeTileSmall from "./ModeTileSmall";
import { RECORDING_MODES } from "../../utils/recordingModes";
import { trackEvent } from "../../utils/analytics/track";
import {
  getRecordingModeDestination,
  getReviewMetadataPath,
} from "../../utils/navigation/recordingModeNav";
import Pulmonology from "../icons/Pulmonology";
import RespiratoryRate from "../icons/RespiratoryRate";
import VoiceSelectionIcon from "../icons/VoiceSelectionIcon";
import StackedLineChartIcon from "../icons/StackedLineChartIcon";
import ResultsModeSlider from "../results/ResultsModeSlider";

const DEFAULT_TIDAL_RECORDING_DURATION_SECONDS = 60;
const TIDAL_RECORDING_DURATION_OPTIONS = [15, 30, 60];

function isDemoModeEnabled() {
  return (
    String(process.env.REACT_APP_DEMO_MODE || "")
      .trim()
      .toLowerCase() === "true"
  );
}

function withRecordingDuration(nextPath, recordingDurationSeconds) {
  const baseOrigin =
    typeof window === "undefined" ? "http://localhost" : window.location.origin;
  const nextUrl = new URL(nextPath, baseOrigin);
  nextUrl.searchParams.set(
    "recordingDurationSeconds",
    String(recordingDurationSeconds),
  );
  return `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`;
}

function ModeIcon({ children }) {
  return (
    <span className="w-fit h-8 [&_svg]:h-8 [&_svg]:w-auto [&_svg]:fill-current [&_svg]:stroke-current">
      {children}
    </span>
  );
}

export default function RecordingModeTiles({ firstRunByVariant, isPermitted }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isTidalDurationDialogOpen, setIsTidalDurationDialogOpen] =
    useState(false);
  const [selectedTidalDurationSeconds, setSelectedTidalDurationSeconds] =
    useState(DEFAULT_TIDAL_RECORDING_DURATION_SECONDS);
  const demoModeEnabled = isDemoModeEnabled();

  const tidalDurationItems = useMemo(
    () =>
      TIDAL_RECORDING_DURATION_OPTIONS.map((seconds) => ({
        value: seconds,
        label: `${seconds}s`,
      })),
    [],
  );

  useEffect(() => {
    if (!isTidalDurationDialogOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsTidalDurationDialogOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isTidalDurationDialogOpen]);

  const buildTile = ({ mode, title, icon, eventName, disabled = false }) => {
    const destination = getRecordingModeDestination(mode, firstRunByVariant);
    const reviewPath = getReviewMetadataPath(destination, mode);

    return (
      <ModeTileSmall
        key={mode}
        to={reviewPath}
        title={title}
        img={icon}
        disabled={disabled}
        onClick={() =>
          trackEvent(eventName, {
            mode,
            destination,
            via: "review_metadata",
          })
        }
      />
    );
  };

  const handleOpenTidalDurationDialog = () => {
    setSelectedTidalDurationSeconds(DEFAULT_TIDAL_RECORDING_DURATION_SECONDS);
    setIsTidalDurationDialogOpen(true);
  };

  const handleCloseTidalDurationDialog = () => {
    setIsTidalDurationDialogOpen(false);
  };

  const handleContinueToTidalRecording = () => {
    const destination = getRecordingModeDestination(
      RECORDING_MODES.TIDAL,
      firstRunByVariant,
    );
    const durationDestination = withRecordingDuration(
      destination,
      selectedTidalDurationSeconds,
    );
    const reviewPath = getReviewMetadataPath(
      durationDestination,
      RECORDING_MODES.TIDAL,
    );

    trackEvent("level_selection_click_tidal_breathing", {
      mode: RECORDING_MODES.TIDAL,
      destination: durationDestination,
      via: "review_metadata",
      recording_duration_seconds: selectedTidalDurationSeconds,
    });

    setIsTidalDurationDialogOpen(false);
    navigate(reviewPath);
  };

  const tiles = [];

  if (isPermitted(RECORDING_MODES.SPIROMETRY)) {
    tiles.push(
      buildTile({
        mode: RECORDING_MODES.SPIROMETRY,
        title: t("levelSelection.spirometryTitle"),
        icon: (
          <ModeIcon>
            <Pulmonology />
          </ModeIcon>
        ),
        eventName: "level_selection_click_spirometry",
      }),
    );
  }

  if (isPermitted(RECORDING_MODES.TIDAL)) {
    if (demoModeEnabled) {
      tiles.push(
        <ModeTileSmall
          key={RECORDING_MODES.TIDAL}
          title={t("levelSelection.tidalBreathingTitle")}
          img={
            <ModeIcon>
              <RespiratoryRate />
            </ModeIcon>
          }
          onClick={handleOpenTidalDurationDialog}
        />,
      );
    } else {
      tiles.push(
        buildTile({
          mode: RECORDING_MODES.TIDAL,
          title: t("levelSelection.tidalBreathingTitle"),
          icon: (
            <ModeIcon>
              <RespiratoryRate />
            </ModeIcon>
          ),
          eventName: "level_selection_click_tidal_breathing",
        }),
      );
    }
  }

  if (isPermitted(RECORDING_MODES.MAUDIO)) {
    tiles.push(
      buildTile({
        mode: RECORDING_MODES.MAUDIO,
        title: t("levelSelection.manualAudioTitle"),
        icon: (
          <ModeIcon>
            <VoiceSelectionIcon />
          </ModeIcon>
        ),
        eventName: "level_selection_click_manual_audio",
      }),
    );
  }

  if (isPermitted(RECORDING_MODES.POSTEXERTION)) {
    tiles.push(
      buildTile({
        mode: RECORDING_MODES.POSTEXERTION,
        title: t("levelSelection.postExertionTitle"),
        icon: (
          <ModeIcon>
            <StackedLineChartIcon />
          </ModeIcon>
        ),
        eventName: "level_selection_click_post_exertion",
      }),
    );
  }

  return (
    <>
      {tiles}

      {isTidalDurationDialogOpen ? (
        <div
          className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/55 backdrop-blur-sm p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="tidal-duration-dialog-title"
          style={{
            paddingTop: "calc(env(safe-area-inset-top, 0px) + 1rem)",
            paddingRight: "calc(env(safe-area-inset-right, 0px) + 1rem)",
            paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 1rem)",
            paddingLeft: "calc(env(safe-area-inset-left, 0px) + 1rem)",
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              handleCloseTidalDurationDialog();
            }
          }}
        >
          <div className="custom-container mt-12 w-full max-w-md rounded shadow p-6 flex flex-col gap-5">
            <div className="flex flex-col gap-2 text-center">
              <h2 id="tidal-duration-dialog-title" className="subtitle-text">
                {t(
                  "levelSelection.durationPickerTitle",
                  "Choose recording length",
                )}
              </h2>
            </div>

            <ResultsModeSlider
              value={selectedTidalDurationSeconds}
              onChange={setSelectedTidalDurationSeconds}
              items={tidalDurationItems}
              ariaLabel={t(
                "levelSelection.durationPickerAriaLabel",
                "Cardiopulmonary recording duration",
              )}
              showLabels={true}
            />

            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                className="outlined-button"
                onClick={handleCloseTidalDurationDialog}
              >
                {t("profile.cancel", "Cancel")}
              </button>
              <button
                type="button"
                className="primary-button"
                onClick={handleContinueToTidalRecording}
              >
                {t("levelSelection.continue")}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
